import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { UserPlus, Shield, KeyRound, Trash2, Power, PowerOff, Edit3, X, Check, Info, Trophy, Copy } from "lucide-react";

function Chip({ children, tone = "default", testid }) {
  const color = tone === "green" ? "#34C759" : tone === "amber" ? "#FFCC00" : tone === "red" ? "#FF3B30" : "#A1A1AA";
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border"
      style={{ color, borderColor: `${color}66`, background: `${color}14` }}
    >
      {children}
    </span>
  );
}

export default function AdminUsersPage() {
  const { user: me } = useAuth();
  const isAdmin = (me?.permissions || []).includes("admin.users");
  const canManageVendors = (me?.permissions || []).includes("vendors.manage");
  const isVendorManagerOnly = !isAdmin && canManageVendors;
  const callerMatchIds = (() => {
    const ma = me?.match_access;
    if (!ma || ma === "all" || (Array.isArray(ma) && (ma.includes("all") || ma.length === 0))) return null;
    return ma;
  })();

  const [catalogue, setCatalogue] = useState(null);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // user object
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [createdCreds, setCreatedCreds] = useState(null); // {email, password, name}
  const [copied, setCopied] = useState("");

  const [form, setForm] = useState({
    email: "", password: "", name: "", designation: "",
    role: "viewer", department: "",
    permissions: [], custom: false,
    matchAccessMode: "all", // "all" | "specific"
    matchIds: [],
    vendorDepartments: [],
  });
  const [matches, setMatches] = useState([]);
  const [preMatchDepts, setPreMatchDepts] = useState([]);

  const load = async () => {
    setErr(""); setOk("");
    const [cat, u, m, d] = await Promise.all([
      api.get("/admin/permissions"),
      api.get("/admin/users"),
      api.get("/matches"),
      api.get("/reference/pre-match-departments?match_id=all"),
    ]);
    setCatalogue(cat.data);
    setUsers(u.data);
    setMatches(m.data);
    setPreMatchDepts([...(d.data.departments || [])].sort((a, b) => a.localeCompare(b)));
  };

  useEffect(() => { load(); }, []);

  const permsByKey = useMemo(() => {
    const m = {};
    (catalogue?.permissions || []).forEach((p) => (m[p.key] = p));
    return m;
  }, [catalogue]);

  const groupedPerms = useMemo(() => {
    const groups = {};
    (catalogue?.permissions || []).forEach((p) => {
      const g = p.key.split(".")[0];
      groups[g] = groups[g] || [];
      groups[g].push(p);
    });
    return groups;
  }, [catalogue]);

  const openCreate = () => {
    setCreating(true);
    setEditing(null);
    const role = isVendorManagerOnly ? "vendor" : "viewer";
    setForm({
      email: "", password: "", name: "", designation: "",
      role, department: "",
      permissions: catalogue?.role_presets?.[role] || [],
      custom: false,
      matchAccessMode: isVendorManagerOnly ? "specific" : "all",
      matchIds: isVendorManagerOnly ? (callerMatchIds || []) : [],
      vendorDepartments: [],
    });
    setErr(""); setOk("");
  };

  const openEdit = (u) => {
    setEditing(u);
    setCreating(false);
    const currentAccess = u.match_access;
    const hasAll = !currentAccess || currentAccess === "all" || (Array.isArray(currentAccess) && (currentAccess.includes("all") || currentAccess.length === 0));
    setForm({
      email: u.email, password: "", name: u.name, designation: u.designation || "",
      role: u.role, department: u.department || "",
      permissions: u.permissions || [],
      custom: !!u.permissions_customised,
      matchAccessMode: hasAll ? "all" : "specific",
      matchIds: hasAll ? [] : (Array.isArray(currentAccess) ? currentAccess : []),
      vendorDepartments: u.vendor_departments || [],
    });
    setErr(""); setOk("");
  };

  const closeForm = () => { setCreating(false); setEditing(null); setErr(""); };

  const onRoleChange = (role) => {
    setForm((f) => ({
      ...f,
      role,
      permissions: catalogue?.role_presets?.[role] || [],
      custom: false,
    }));
  };

  const togglePerm = (key) => {
    setForm((f) => {
      const has = f.permissions.includes(key);
      return {
        ...f,
        permissions: has ? f.permissions.filter((p) => p !== key) : [...f.permissions, key],
        custom: true,
      };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOk("");
    try {
      const matchAccessPayload = form.matchAccessMode === "all" ? ["all"] : form.matchIds;
      if (form.role === "vendor" && form.vendorDepartments.length === 0) {
        setErr("Vendor accounts must be assigned to at least one department.");
        return;
      }
      if (creating) {
        await api.post("/admin/users", {
          email: form.email,
          password: form.password,
          name: form.name,
          designation: form.designation,
          role: form.role,
          department: form.department,
          permissions: form.custom ? form.permissions : null,
          match_access: matchAccessPayload,
          vendor_departments: form.role === "vendor" ? form.vendorDepartments : [],
        });
        setCreatedCreds({ email: form.email, password: form.password, name: form.name });
        setOk(`User ${form.email} created.`);
      } else if (editing) {
        const payload = {
          name: form.name,
          designation: form.designation,
          department: form.department,
          role: form.role,
          permissions: form.custom ? form.permissions : null,
          match_access: matchAccessPayload,
          vendor_departments: form.role === "vendor" ? form.vendorDepartments : [],
        };
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/users/${editing.id}`, payload);
        setOk(`User ${editing.email} updated.`);
      }
      closeForm();
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed");
    }
  };

  const toggleActive = async (u) => {
    setErr(""); setOk("");
    try {
      await api.patch(`/admin/users/${u.id}`, { active: !(u.active !== false) });
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    setErr(""); setOk("");
    try {
      await api.delete(`/admin/users/${u.id}`);
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed");
    }
  };

  if (!catalogue) return <div className="p-8 text-zinc-500 font-mono-live">▓▒░ loading admin ░▒▓</div>;

  const showForm = creating || editing;
  const isEditingSelf = editing?.id === me?.id;

  return (
    <div className="p-6 md:p-10 pb-20" data-testid="page-admin-users">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 border-b border-cmdBorder pb-6">
        <div>
          <div className="mc-label">Admin · Identity &amp; Access</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2">User Management</h1>
          <p className="text-zinc-400 mt-2 text-sm max-w-2xl">Add new logins, assign custom designations, choose a preset role or hand-pick capabilities per user.</p>
        </div>
        <button
          onClick={openCreate}
          className="bg-white text-black px-4 py-2 font-bold uppercase tracking-widest text-xs flex items-center gap-2"
          data-testid="admin-new-user-btn"
        >
          <UserPlus className="w-4 h-4" /> Add User
        </button>
      </div>

      {(err || ok) && (
        <div className={`mc-panel p-3 mb-4 border-l-4 ${err ? "border-incidentRed" : "border-readyGreen"}`} data-testid="admin-notice">
          <div className="text-sm" style={{ color: err ? "#FF3B30" : "#34C759" }}>{err || ok}</div>
        </div>
      )}

      {/* Credentials-created modal — shown once after every successful create so admin can copy & share */}
      {createdCreds && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="created-creds-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setCreatedCreds(null)} />
          <div className="relative mc-panel border-l-4 border-readyGreen p-6 w-full max-w-lg shadow-2xl">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="mc-label text-readyGreen">Login Created</div>
                <h2 className="font-head font-black text-2xl uppercase tracking-tight leading-none mt-1">Share credentials</h2>
                <p className="text-xs text-zinc-400 mt-2">Copy and send these to <span className="text-white">{createdCreds.name}</span>. The password won't be shown again.</p>
              </div>
              <button
                onClick={() => setCreatedCreds(null)}
                className="border border-cmdBorder hover:border-white p-2"
                data-testid="created-creds-close"
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-3">
              {[
                { label: "Login URL", value: window.location.origin, key: "url" },
                { label: "Email",     value: createdCreds.email,     key: "email" },
                { label: "Password",  value: createdCreds.password,  key: "password", mono: true },
              ].map((row) => (
                <div key={row.key} className="border border-cmdBorder p-3 flex items-center gap-3" data-testid={`created-creds-row-${row.key}`}>
                  <div className="flex-1 min-w-0">
                    <div className="mc-label">{row.label}</div>
                    <div className={`text-sm mt-1 truncate ${row.mono ? "font-mono-live text-warnAmber" : "text-white"}`}>{row.value}</div>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      await navigator.clipboard.writeText(row.value);
                      setCopied(row.key);
                      setTimeout(() => setCopied((c) => (c === row.key ? "" : c)), 1500);
                    }}
                    className="border border-cmdBorder hover:border-readyGreen hover:text-readyGreen px-3 py-2 text-xs uppercase tracking-widest transition-colors flex items-center gap-1 shrink-0"
                    data-testid={`created-creds-copy-${row.key}`}
                  >
                    {copied === row.key ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                    {copied === row.key ? "Copied" : "Copy"}
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={async () => {
                const block = `MatchCommand access\n\nLogin URL: ${window.location.origin}\nEmail: ${createdCreds.email}\nPassword: ${createdCreds.password}`;
                await navigator.clipboard.writeText(block);
                setCopied("all");
                setTimeout(() => setCopied((c) => (c === "all" ? "" : c)), 1500);
              }}
              className="mt-4 w-full bg-readyGreen text-white px-4 py-2 font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 hover:brightness-110"
              data-testid="created-creds-copy-all"
            >
              {copied === "all" ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied === "all" ? "All three copied" : "Copy all (ready to paste in email / WhatsApp)"}
            </button>
            <div className="text-[11px] text-warnAmber mt-3 border-l-2 border-warnAmber pl-2">
              Tell the user to change the password after their first login.
            </div>
          </div>
        </div>
      )}

      {/* Access criteria */}
      <div className="mc-panel p-4 mb-6" data-testid="admin-criteria">
        <div className="flex items-start gap-2">
          <Info className="w-4 h-4 text-warnAmber mt-1" />
          <div>
            <div className="mc-label mb-2">Criteria for Granting Access</div>
            <ul className="text-xs text-zinc-400 space-y-1 list-disc pl-4">
              {catalogue.access_criteria.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        </div>
      </div>

      {/* User table */}
      <div className="mc-panel overflow-x-auto mb-6" data-testid="admin-users-table">
        <table className="w-full mc-table">
          <thead>
            <tr>
              <th>Name / Email</th>
              <th>Designation</th>
              <th>Role</th>
              <th>Perms</th>
              <th>Matches</th>
              <th>Status</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isMe = u.id === me?.id;
              const isActive = u.active !== false;
              return (
                <tr key={u.id} data-testid={`admin-user-row-${u.email}`}>
                  <td>
                    <div className="font-medium flex items-center gap-2">
                      {u.name}
                      {isMe && <span className="text-[9px] uppercase tracking-widest border border-readyGreen text-readyGreen px-1.5">You</span>}
                    </div>
                    <div className="text-xs text-zinc-500 font-mono-live mt-0.5">{u.email}</div>
                  </td>
                  <td className="text-zinc-300">{u.designation || <span className="text-zinc-600">—</span>}</td>
                  <td>
                    <Chip tone={u.role === "admin" ? "green" : u.role === "match_director" ? "amber" : "default"}>
                      {u.role.replace(/_/g, " ")}
                    </Chip>
                  </td>
                  <td>
                    <span className="font-mono-live text-xs text-zinc-300">{(u.permissions || []).length}</span>
                    {u.permissions_customised && <span className="ml-2 text-[9px] uppercase tracking-widest text-warnAmber">custom</span>}
                  </td>
                  <td data-testid={`admin-user-matches-${u.email}`}>
                    {(() => {
                      const ma = u.match_access;
                      const isAll = !ma || ma === "all" || (Array.isArray(ma) && (ma.includes("all") || ma.length === 0 && ma !== null));
                      if (isAll) return <Chip tone="green">All</Chip>;
                      const list = Array.isArray(ma) ? ma : [];
                      if (list.length === 0) return <Chip tone="red">None</Chip>;
                      return <span className="font-mono-live text-xs text-zinc-300">{list.length}</span>;
                    })()}
                  </td>
                  <td>
                    {isActive
                      ? <Chip tone="green" testid={`admin-status-active-${u.email}`}>Active</Chip>
                      : <Chip tone="red" testid={`admin-status-inactive-${u.email}`}>Deactivated</Chip>}
                  </td>
                  <td className="text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => openEdit(u)}
                        className="border border-cmdBorder hover:border-white px-2 py-1 text-xs uppercase tracking-widest transition-colors flex items-center gap-1"
                        data-testid={`admin-edit-${u.email}`}
                      >
                        <Edit3 className="w-3 h-3" /> Edit
                      </button>
                      <button
                        onClick={() => toggleActive(u)}
                        disabled={isMe}
                        title={isMe ? "Cannot change your own status" : ""}
                        className={`border px-2 py-1 text-xs uppercase tracking-widest transition-colors flex items-center gap-1 ${
                          isActive
                            ? "border-cmdBorder hover:border-warnAmber hover:text-warnAmber"
                            : "border-readyGreen text-readyGreen hover:bg-readyGreen hover:text-black"
                        } disabled:opacity-40 disabled:cursor-not-allowed`}
                        data-testid={`admin-toggle-active-${u.email}`}
                      >
                        {isActive ? <><PowerOff className="w-3 h-3" /> Deactivate</> : <><Power className="w-3 h-3" /> Activate</>}
                      </button>
                      <button
                        onClick={() => remove(u)}
                        disabled={isMe}
                        title={isMe ? "Cannot delete your own account" : ""}
                        className="border border-cmdBorder hover:border-incidentRed hover:text-incidentRed px-2 py-1 text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`admin-delete-${u.email}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Create / Edit form */}
      {showForm && (
        <form onSubmit={submit} className="mc-panel p-6 space-y-5" data-testid="admin-user-form">
          <div className="flex items-center justify-between">
            <div>
              <div className="mc-label">{creating ? "New User" : `Editing ${editing.email}`}</div>
              <h2 className="font-head font-bold text-2xl uppercase mt-1">{creating ? "Add User" : "Edit User"}</h2>
            </div>
            <button type="button" onClick={closeForm} className="text-zinc-400 hover:text-white" data-testid="admin-form-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="mc-label">Email</label>
              <input
                type="email"
                required
                disabled={!!editing}
                className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1 font-mono-live disabled:opacity-60"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="admin-form-email"
              />
            </div>
            <div>
              <label className="mc-label">Full Name</label>
              <input
                required
                className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="admin-form-name"
              />
            </div>
            <div>
              <label className="mc-label">Designation <span className="text-zinc-600 normal-case tracking-normal">(custom title)</span></label>
              <input
                required
                placeholder="e.g., Head of Signages, VVIP Liaison, Broadcast Manager"
                className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                value={form.designation}
                onChange={(e) => setForm({ ...form, designation: e.target.value })}
                data-testid="admin-form-designation"
              />
            </div>
            <div>
              <label className="mc-label">Department / Vendor name</label>
              <input
                placeholder="Optional"
                className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
                data-testid="admin-form-dept"
              />
            </div>
            <div>
              <label className="mc-label">Role Preset</label>
              <select
                className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1 disabled:opacity-60"
                value={form.role}
                onChange={(e) => onRoleChange(e.target.value)}
                disabled={isVendorManagerOnly}
                data-testid="admin-form-role"
              >
                {Object.keys(catalogue.role_presets)
                  .filter((r) => (isVendorManagerOnly ? r === "vendor" : true))
                  .map((r) => (
                    <option key={r} value={r}>{r.replace(/_/g, " ")}</option>
                  ))}
              </select>
              <p className="text-xs text-zinc-500 mt-1">
                {isVendorManagerOnly
                  ? "You can only create Vendor accounts."
                  : "Selecting a role auto-checks its permissions. You can then hand-pick below."}
              </p>
            </div>
            <div>
              <label className="mc-label">{editing ? "New Password (optional)" : "Password"}</label>
              <input
                type="password"
                required={creating}
                placeholder={editing ? "Leave blank to keep current" : "Min 8 characters"}
                className="w-full bg-black border border-cmdBorder p-2 text-sm mt-1 font-mono-live"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="admin-form-password"
              />
            </div>
          </div>

          {/* Permissions — hidden for vendor managers (they can't customise) */}
          {!isVendorManagerOnly && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="mc-label flex items-center gap-2"><Shield className="w-3 h-3" /> Capabilities</div>
                <p className="text-xs text-zinc-500 mt-1">
                  {form.custom
                    ? <span className="text-warnAmber">Customised — no longer matches preset</span>
                    : <span>Using <span className="text-white">{form.role.replace(/_/g, " ")}</span> preset ({form.permissions.length} permissions).</span>}
                </p>
              </div>
              <div className="font-mono-live text-xs text-zinc-400">
                {form.permissions.length} / {catalogue.permissions.length}
              </div>
            </div>
            <div className="border border-cmdBorder divide-y divide-cmdBorder">
              {Object.entries(groupedPerms).map(([group, perms]) => (
                <div key={group} className="p-3">
                  <div className="mc-label mb-2">{group}</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                    {perms.map((p) => {
                      const checked = form.permissions.includes(p.key);
                      return (
                        <label
                          key={p.key}
                          className={`flex items-start gap-2 px-2 py-1.5 border cursor-pointer transition-colors ${
                            checked ? "border-readyGreen bg-readyGreen/10" : "border-cmdBorder hover:border-white"
                          }`}
                          data-testid={`admin-perm-${p.key}`}
                        >
                          <input
                            type="checkbox"
                            className="mt-1 accent-white"
                            checked={checked}
                            onChange={() => togglePerm(p.key)}
                          />
                          <div>
                            <div className="text-xs font-mono-live">{p.key}</div>
                            <div className="text-[11px] text-zinc-500">{p.description}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Match Access */}
          <div>
            <div className="mc-label flex items-center gap-2 mb-2"><Trophy className="w-3 h-3" /> Match Access</div>
            <p className="text-xs text-zinc-500 mb-2">Choose which matches this user can see and act on. Users without access are denied at the API layer.</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {[
                { key: "all", label: "All Matches" },
                { key: "specific", label: "Specific Matches" },
              ].filter((opt) => !isVendorManagerOnly || opt.key === "specific").map((opt) => (
                <label
                  key={opt.key}
                  className={`px-3 py-1.5 text-xs uppercase tracking-widest font-bold border cursor-pointer transition-colors ${
                    form.matchAccessMode === opt.key
                      ? "bg-white text-black border-white"
                      : "border-cmdBorder text-zinc-400 hover:border-white"
                  }`}
                >
                  <input
                    type="radio"
                    className="sr-only"
                    checked={form.matchAccessMode === opt.key}
                    onChange={() => setForm((f) => ({ ...f, matchAccessMode: opt.key }))}
                    data-testid={`admin-match-mode-${opt.key}`}
                  />
                  {opt.label}
                </label>
              ))}
              {isVendorManagerOnly && (
                <span className="text-[10px] uppercase tracking-widest font-bold text-readyGreen border border-readyGreen/50 px-3 py-1.5">
                  Locked to your assigned matches
                </span>
              )}
            </div>
            {form.matchAccessMode === "specific" && (
              <div className="border border-cmdBorder p-3 grid grid-cols-1 md:grid-cols-2 gap-1.5" data-testid="admin-match-list">
                {matches.length === 0 && <div className="text-xs text-zinc-500 col-span-2">No matches available.</div>}
                {matches
                  .filter((m) => !isVendorManagerOnly || !callerMatchIds || callerMatchIds.includes(m.id))
                  .map((m) => {
                  const checked = form.matchIds.includes(m.id);
                  return (
                    <label
                      key={m.id}
                      className={`flex items-start gap-2 px-2 py-1.5 border cursor-pointer transition-colors ${
                        checked ? "border-readyGreen bg-readyGreen/10" : "border-cmdBorder hover:border-white"
                      }`}
                      data-testid={`admin-match-${m.id}`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1 accent-white"
                        checked={checked}
                        onChange={() =>
                          setForm((f) => ({
                            ...f,
                            matchIds: checked
                              ? f.matchIds.filter((x) => x !== m.id)
                              : [...f.matchIds, m.id],
                          }))
                        }
                      />
                      <div>
                        <div className="text-xs font-medium">{m.name}</div>
                        <div className="text-[11px] text-zinc-500 font-mono-live">{m.venue} · {m.status}</div>
                      </div>
                    </label>
                  );
                })}
                {form.matchIds.length === 0 && (
                  <div className="col-span-2 text-xs text-warnAmber mt-1">Selecting no matches will lock this user out of every match.</div>
                )}
              </div>
            )}
          </div>

          {/* Vendor Departments (only when role == vendor) */}
          {form.role === "vendor" && (
            <div>
              <div className="mc-label flex items-center gap-2 mb-2"><Shield className="w-3 h-3" /> Vendor Departments</div>
              <p className="text-xs text-zinc-500 mb-2">Choose one or more pre-match departments this vendor is responsible for. They will only see Pre-Match Tasks & Issues routed to these departments and can only <span className="text-white">Mark Completed / Mark Resolved</span>.</p>
              <div className="border border-cmdBorder p-3 grid grid-cols-1 md:grid-cols-3 gap-1.5" data-testid="admin-vendor-dept-list">
                {preMatchDepts.length === 0 && <div className="text-xs text-zinc-500 col-span-3">Loading departments…</div>}
                {preMatchDepts.map((d) => {
                  const checked = form.vendorDepartments.includes(d);
                  return (
                    <label
                      key={d}
                      className={`flex items-center gap-2 px-2 py-1.5 border cursor-pointer transition-colors ${
                        checked ? "border-readyGreen bg-readyGreen/10" : "border-cmdBorder hover:border-white"
                      }`}
                      data-testid={`admin-vendor-dept-${d}`}
                    >
                      <input
                        type="checkbox"
                        className="accent-white"
                        checked={checked}
                        onChange={() =>
                          setForm((f) => ({
                            ...f,
                            vendorDepartments: checked
                              ? f.vendorDepartments.filter((x) => x !== d)
                              : [...f.vendorDepartments, d],
                          }))
                        }
                      />
                      <span className="text-xs font-medium">{d}</span>
                    </label>
                  );
                })}
              </div>
              {form.vendorDepartments.length === 0 && (
                <div className="text-xs text-warnAmber mt-2">Select at least one department, otherwise the vendor will see nothing.</div>
              )}
            </div>
          )}

          <div className="flex gap-3 items-center pt-2">
            <button
              type="submit"
              disabled={isEditingSelf && !form.custom && form.role !== editing?.role}
              className="bg-white text-black px-4 py-2 font-bold uppercase tracking-widest text-xs flex items-center gap-2 disabled:opacity-50"
              data-testid="admin-form-submit"
            >
              <Check className="w-4 h-4" /> {creating ? "Create User" : "Save Changes"}
            </button>
            <button type="button" onClick={closeForm} className="border border-cmdBorder px-4 py-2 text-xs uppercase tracking-widest">
              Cancel
            </button>
            {form.custom && (
              <button
                type="button"
                onClick={() => onRoleChange(form.role)}
                className="ml-auto flex items-center gap-1 text-xs uppercase tracking-widest text-warnAmber border border-warnAmber/40 hover:bg-warnAmber hover:text-black px-3 py-2 transition-colors"
                data-testid="admin-form-reset-preset"
              >
                <KeyRound className="w-3 h-3" /> Reset to {form.role.replace(/_/g, " ")} preset
              </button>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
